/**
 * Rupture conventionnelle / Licenciement — coût employeur réaliste.
 *
 * 02/06/2026 — Remplace le simple `employerCost * 0.5` par un calcul
 * structuré inspiré du droit social français :
 *   • Indemnité spécifique de rupture conventionnelle (ISRC) ou
 *     indemnité légale de licenciement, selon ancienneté.
 *   • Indemnité compensatrice de préavis (2 mois max).
 *   • Indemnité de congés payés (estimation 10 % du salaire annuel).
 *   • Charges patronales sur l'indemnité (~42 % en France).
 *
 * Le joueur peut choisir entre **rupture conventionnelle** (mode par défaut,
 * encouragé) ou **licenciement classique** :
 *   - Rupture conventionnelle : préavis non dû (négocié), indemnité minimum
 *     légale ¼ mois × ancienneté (10 premières années) + ⅓ mois × au-delà.
 *   - Licenciement (cause réelle et sérieuse) : préavis dû (2 mois),
 *     indemnité légale identique mais sans charges sur la première partie.
 *
 * Pour la simulation on garde le modèle simplifié : ancienneté < 8 mois →
 * période d'essai, simple forfait ½ mois. Sinon calcul ISRC + charges.
 *
 * Tous les montants sont en euros entiers (Math.round).
 */

const MS_PER_DAY = 1; // we work in "game days"
const DAYS_PER_MONTH = 30; // gameplay convention used elsewhere

/**
 * @param {Object} driver - { hiredAt, employerCost }
 * @param {number} totalDays - current game day
 * @param {'rupture'|'fault'} [mode='rupture']
 * @returns {{
 *   total: number,
 *   breakdown: {
 *     indemnity: number,
 *     notice: number,
 *     paidLeave: number,
 *     charges: number,
 *   },
 *   seniorityMonths: number,
 *   mode: 'probation'|'rupture'|'fault',
 *   monthlyGross: number,
 * }}
 */
export function computeSeveranceCost(driver, totalDays, mode = 'rupture') {
  const employerCost = Math.max(0, driver?.employerCost || 0);
  const hiredAt = Math.max(0, driver?.hiredAt || 0);
  const seniorityDays = Math.max(0, (totalDays || 0) - hiredAt);
  const seniorityMonths = seniorityDays / DAYS_PER_MONTH;

  // employerCost dans HST = coût total mensuel salaires + charges (~ x1.42 du brut).
  // On retro-calcule un brut mensuel approximatif pour le calcul de l'ISRC.
  const monthlyGross = Math.round(employerCost / 1.42);

  // === Cas 1 : Période d'essai (< 8 mois d'ancienneté) ===
  // En France la rupture en période d'essai n'ouvre pas droit à indemnité de
  // licenciement. On garde un coût administratif minimal (½ mois employeur)
  // pour représenter le manque-à-gagner et la solde de tout compte.
  if (seniorityMonths < 8) {
    const total = Math.round(employerCost * 0.5);
    return {
      total,
      breakdown: { indemnity: 0, notice: 0, paidLeave: total, charges: 0 },
      seniorityMonths,
      mode: 'probation',
      monthlyGross,
    };
  }

  // === Cas 2 : ISRC / Indemnité légale de licenciement ===
  // ¼ mois × ancienneté (10 premières années) + ⅓ mois × au-delà.
  const years = seniorityMonths / 12;
  const yearsCapped10 = Math.min(years, 10);
  const yearsAbove10 = Math.max(0, years - 10);
  const indemnity = Math.round(
    monthlyGross * 0.25 * yearsCapped10
    + monthlyGross * (1 / 3) * yearsAbove10,
  );

  // === Préavis ===
  // Rupture conventionnelle : pas de préavis (négocié), 0 €.
  // Licenciement classique : 2 mois de salaire brut (cadre + ancienneté > 2 ans).
  const noticeMonths = mode === 'fault' ? 2 : 0;
  const notice = Math.round(monthlyGross * noticeMonths);

  // === Indemnité de congés payés ===
  // 10 % du salaire brut accumulé sur les 12 derniers mois.
  // Approximation : on capse à 1 mois.
  const paidLeave = Math.round(monthlyGross * Math.min(1, years) * 0.1);

  // === Charges patronales sur l'indemnité ===
  // L'ISRC < 2 PASS est exonérée de cotisations sociales (~80 % des cas)
  // mais soumise à CSG/CRDS et au forfait social (~30 % du dépassement).
  // Le préavis est lui pleinement chargé (~42 %).
  // Simplification : 12 % sur indemnité (CSG/CRDS + forfait social moyen),
  // 42 % sur préavis et congés payés.
  const charges = Math.round(
    indemnity * 0.12 + (notice + paidLeave) * 0.42,
  );

  const total = indemnity + notice + paidLeave + charges;

  return {
    total,
    breakdown: { indemnity, notice, paidLeave, charges },
    seniorityMonths,
    mode,
    monthlyGross,
  };
}

/**
 * Court résumé textuel utilisé dans le message Nathalie et la notification.
 * 02/06/2026 — i18n-aware via `tStandalone` afin que les utilisateurs EN
 * voient le récap en anglais. La langue est lue depuis `useLanguageStore`.
 */
export function formatSeveranceSummary(result, driverName) {
  if (!result) return '';
  // Lazy import to avoid circular dependency with i18n init.
  // eslint-disable-next-line global-require
  const { tStandalone } = require('../../i18n');
  const { total, breakdown, seniorityMonths, mode, monthlyGross } = result;
  const months = seniorityMonths.toFixed(1);
  const eur = (n) => `${Math.round(n).toLocaleString('fr-FR')} €`;

  const vars = {
    driverName,
    months,
    total: eur(total),
    gross: eur(monthlyGross),
    indemnity: eur(breakdown.indemnity),
    notice: eur(breakdown.notice),
    paidLeave: eur(breakdown.paidLeave),
    charges: eur(breakdown.charges),
  };

  if (mode === 'probation') return tStandalone('severance_summary_probation', vars);
  if (mode === 'fault') return tStandalone('severance_summary_fault', vars);
  return tStandalone('severance_summary_rupture', vars);
}
